import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { RightOutlined, ToolOutlined, SyncOutlined } from '@ant-design/icons';
import { Text, Button, CustomSVG, ConfirmModal } from '../../../components';
import { NewRMCard } from '../NewRMCard';
import { useAppliancesQuery, useUpgradeApplianceMutation } from '../../../api';
import {
    getApplianceReadiness,
    canApplianceUpgrade,
    APPLIANCE_READINESS_STATE_INFO,
} from '../../../pages/Clouds/utils';
import { ROUTE_CONFIG } from '../../../constants/routes';
import { CLOUD_TYPES } from '../../../constants/cloudTypes';
import Bullseye from '../../../assets/icons/bullseye.svg?raw';
import UpArrowSquareIcon from '../../../assets/icons/up-arrow-square.svg?raw';
import { useScrollMask } from '../../ScrollMask/useScrollMask';
import { useNotificationContext } from '../../../providers';
import classNames from 'classnames';
import './LandingZonesCard.scss';

const getCloudTargetLabel = (type) => CLOUD_TYPES.find((t) => t.value === type)?.label ?? type;

export const LandingZonesCard = () => {
    const userOrg = sessionStorage.getItem('userOrg');
    const { data, isLoading } = useAppliancesQuery({ userOrg }, { skip: !userOrg });
    const [upgradeAppliance] = useUpgradeApplianceMutation();
    const { notifySuccess, notifyError } = useNotificationContext();
    const appliances = data?.content ?? [];
    const [showConfirmUpgradeAppliance, setShowConfirmUpgradeAppliance] = useState(false);
    const [selectedUpgradeAppliance, setSelectedUpgradeAppliance] = useState(null);
    const [scrollRef, maskClass] = useScrollMask([appliances.length]);

    return (
        <>
            <NewRMCard className="landing-zones-card" showMoreIcon={false} loading={isLoading}>
                <header>
                    <Text size="large" color="secondary">
                        Migration Appliances
                    </Text>
                </header>
                <div className="landing-zones-body">
                    <div className="landing-zones-left">
                        <div className="landing-zones-count-section">
                            <span className="landing-zones-count-label">
                                <CustomSVG svg={Bullseye} className="landing-zones-icon" />
                                <Text size="small" color="muted" bold>
                                    Appliances
                                </Text>
                            </span>
                            <Text size="extraLarge" xxlBold color="primaryBlue" className="landing-zones-count">
                                {appliances.length}
                            </Text>
                        </div>
                        <Link to={ROUTE_CONFIG.clouds.path}>
                            <Button type="primary">
                                View Appliances <RightOutlined />
                            </Button>
                        </Link>
                    </div>
                    <div ref={scrollRef} className={classNames('landing-zones-right', maskClass)}>
                        {appliances.map((item) => (
                            <div className="appliance-row" key={item.id}>
                                <div className="appliance-cell appliance-name">
                                    <span className="cell-value">
                                        <ToolOutlined className="row-icon" />
                                        <Link
                                            to={ROUTE_CONFIG.clouds.path}
                                            state={{ applianceId: item.id }}
                                            className="appliance-name-link"
                                        >
                                            {item.name}
                                        </Link>
                                    </span>
                                </div>
                                <div className="appliance-cell">
                                    <span className="cell-label">Cloud Target</span>
                                    <Text size="small" extraBold color="muted" className="cell-value">
                                        {getCloudTargetLabel(item.type)}
                                    </Text>
                                </div>
                                <div className="appliance-cell">
                                    <span className="cell-label">Readiness</span>
                                    <span className="cell-value">
                                        {getApplianceReadiness(item, APPLIANCE_READINESS_STATE_INFO)}
                                    </span>
                                </div>
                                <div className="appliance-cell">
                                    <span className="cell-label">Version</span>
                                    <span className="cell-value">
                                        <Text size="small" extraBold color="muted">
                                            {item.appliance?.appliance_version_num || '—'}
                                        </Text>
                                        {canApplianceUpgrade(item) && (
                                            <CustomSVG svg={UpArrowSquareIcon} className="upgrade-icon" />
                                        )}
                                    </span>
                                </div>
                                <div className="appliance-cell update-cell">
                                    {canApplianceUpgrade(item) && (
                                        <span
                                            className="update-link"
                                            onClick={() => {
                                                setSelectedUpgradeAppliance(item);
                                                setShowConfirmUpgradeAppliance(true);
                                            }}
                                        >
                                            Update <SyncOutlined />
                                        </span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </NewRMCard>
            <ConfirmModal
                title="Update Migration Appliance"
                open={showConfirmUpgradeAppliance}
                onCancel={() => setShowConfirmUpgradeAppliance(false)}
                onContinue={async () => {
                    setShowConfirmUpgradeAppliance(false);
                    await upgradeAppliance({ applianceId: selectedUpgradeAppliance?.appliance?.id })
                        .unwrap()
                        .then(() =>
                            notifySuccess({ message: 'Success', description: 'Appliance upgrade is in progress' }),
                        )
                        .catch((error) =>
                            notifyError({
                                message: 'Error',
                                description: `Error while upgrading appliance. ${error.message}`,
                            }),
                        );
                }}
                ghostCancel
            >
                Update <strong>{selectedUpgradeAppliance?.name}</strong> to the latest version?
            </ConfirmModal>
        </>
    );
};
